import type { ReactNode } from "react"
import type { ColumnDef, ColumnFilter } from "@tanstack/react-table"

import type { ConvertNumberType } from "~/ui-rtk/utils/number-utils"

export type TMetric = Record<string, unknown>

export type TTextOperator = "contains" | "notContains"
export interface ColumnFilterExtended extends ColumnFilter {
  operator?: TTextOperator
  idx?: number
}

export const FILTER_OPERATOR: Record<TTextOperator, string> = {
  contains: "Contains",
  notContains: "Excludes",
}

export type TableColumnType =
  | ConvertNumberType
  | "auto"
  | "image"
  | "title"
  | "date"
  | "text"
  | "tags"
  | "boolean"
  | undefined

export type TableVisualizationColumn = {
  key: string
  header?: string
  type?: TableColumnType
  width?: number
  disabled?: boolean
  disableSorting?: boolean
  disableFiltering?: boolean
  render?: (
    row: TMetric,
    renderConfig?: Record<string, unknown>,
  ) => ReactNode | string
  className?: string
  isBrand?: boolean
  percentSize?: string
  classes?: Partial<{
    cell: string
    head: string
  }>
  highlightFn?: (val: number) => string
  extraSorting?: {
    id: string
    desc: boolean
  }
  // Tagging system property
  taggableId?: string
  // Metric dictionary key for tooltip descriptions
  metricKey?: string
  // Blur this column's cells when demo mode is enabled (use for identifying
  // fields whose key doesn't already match the BodyCell auto-blur keywords).
  blurInDemo?: boolean
  /**
   * CSV export only: the column is a percentage computed from two fields of
   * the exported row (`numerator / denominator * 100`, `complement` flips it
   * to the abandon rate `100 - rate`). Lets a rate the UI derives client-side
   * ride along in a worker export that only sees raw Cube rows.
   */
  csvDerived?: CsvDerivedColumn
  /**
   * CSV export only: row field to read when the column's own field is empty
   * (a collection with no title exports its handle, as the cell shows it).
   */
  csvFallbackKey?: string
}

export type CsvDerivedColumn = {
  numerator: string
  denominator: string
  complement?: boolean
}

export type TSource = {
  name: string
  icon: string
  isConnected: boolean
}

export type TSources = Map<string, TSource>

export type TableVisualizationColumnFilter = {
  variant: "text" | "range" | "date" | "select"
} & ColumnFilterExtended

export type TableVisualizationColumnDef = ColumnDef<TMetric, unknown> &
  TableVisualizationColumn & {
    meta?: {
      filterFn?: (filter: TableVisualizationColumnFilter) => void
    }
  }

export type TColumnFiltersState = TableVisualizationColumnFilter[]

export type TableVisualizationMeta = {
  filterFn: (state: TColumnFiltersState) => void
  getFiltersStateFn: () => TColumnFiltersState
  addFilterFn: (columnFilter: TableVisualizationColumnFilter) => void
  resetFilters: () => void
}

export const HIGHLIGHT_CSS_CLASS = {
  TOP_10: "bg-green/50",
  TOP_20: "bg-green/25",
  TOP_30: "bg-green/10",
  BOTTOM_10: "bg-red/50",
  BOTTOM_20: "bg-red/30",
  BOTTOM_30: "bg-red/20",
}
